import React from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';

// Research Laboratory Animations
const hologramRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const scanBeam = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ringPulse = keyframes`
  0% { r: 30; opacity: 0.8; }
  100% { r: 50; opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const screenFlicker = keyframes`
  0%, 100% { opacity: 0.85; }
  50% { opacity: 0.95; }
`;

const particleRise = keyframes`
  from { transform: translateY(0px); opacity: 1; }
  to { transform: translateY(-50px); opacity: 0; }
`;

/**
 * CleanTech Orbital Spinner - Research Laboratory
 * Light Mode Scientific Style
 */
export const CleanTechOrbitalSpinner = ({ size = 200, message = null }) => {
  const hologramSize = size * 0.4;
  const blockSize = size * 0.25;
  const panelWidth = size * 0.3;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        width: size * 2.5,
        height: size * 1.4,
        position: 'relative',
        background: 'linear-gradient(to bottom, #F0F8FF 0%, #FFFFFF 50%)',
        overflow: 'hidden',
      }}
    >
      {/* Background Lab Environment (blurred) */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(240, 248, 255, 0.5) 0%, rgba(224, 240, 255, 0.3) 100%)',
          filter: 'blur(20px)',
        }}
      />

      {/* LEFT SIDE - 3D Geometric Blocks */}
      <Box
        sx={{
          position: 'absolute',
          left: '10%',
          top: '30%',
          width: blockSize,
          height: blockSize,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          zIndex: 1,
        }}
      >
        {[
          { delay: 0, color: '#FF6B35' },
          { delay: 1, color: '#FF8C42' },
          { delay: 2, color: '#FFA756' }
        ].map((block, i) => (
          <Box
            key={i}
            sx={{
              width: blockSize * 0.6,
              height: blockSize * 0.4,
              background: `linear-gradient(135deg, ${block.color} 0%, ${block.color}dd 100%)`,
              borderRadius: 1,
              boxShadow: `0 10px 20px rgba(255, 107, 53, 0.3)`,
              animation: `${float} 3s ease-in-out infinite`,
              animationDelay: `${block.delay}s`,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(45deg, transparent 40%, rgba(255, 255, 255, 0.3) 50%, transparent 60%)',
                borderRadius: 1,
              }
            }}
          />
        ))}
      </Box>

      {/* RIGHT TOP - Transparent Panel */}
      <Box
        sx={{
          position: 'absolute',
          right: '5%',
          top: '10%',
          width: panelWidth,
          height: size * 0.6,
          background: 'linear-gradient(135deg, rgba(0, 168, 255, 0.15) 0%, rgba(0, 119, 204, 0.1) 100%)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(0, 255, 255, 0.4)',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 168, 255, 0.2), inset 0 0 20px rgba(0, 255, 255, 0.1)',
          padding: 1.5,
          animation: `${screenFlicker} 4s ease-in-out infinite`,
          zIndex: 1,
        }}
      >
        {/* Global Map Visualization */}
        <Box sx={{ mb: 1 }}>
          <svg width="100%" height="60" viewBox="0 0 100 60">
            {/* World Map Outline (simplified) */}
            <path
              d="M10,30 L20,25 L35,28 L40,20 L55,22 L65,18 L75,25 L85,20 L90,28"
              fill="none"
              stroke="#0077CC"
              strokeWidth="1.5"
              opacity="0.6"
            />
            {/* Data Points */}
            {[[20, 25], [40, 20], [65, 18], [85, 20]].map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="2" fill="#00A8FF">
                <animate
                  attributeName="opacity"
                  values="0.5;1;0.5"
                  dur="2s"
                  begin={`${i * 0.3}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}
          </svg>
        </Box>

        {/* Data Streams */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {[85, 70, 95, 60].map((width, i) => (
            <Box key={i} sx={{ height: 3, background: 'rgba(0, 168, 255, 0.2)', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{
                height: '100%',
                width: `${width}%`,
                background: 'linear-gradient(90deg, #00A8FF, #0077CC)',
                boxShadow: '0 0 5px #00A8FF',
              }} />
            </Box>
          ))}
        </Box>

        {/* Mini Charts */}
        <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
          {[60, 80, 70, 90, 65].map((height, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                height: `${height}%`,
                background: 'linear-gradient(to top, #00A8FF, #00FFFF)',
                opacity: 0.6,
                borderRadius: 0.5,
              }}
            />
          ))}
        </Box>

        {/* Edge Glow */}
        <Box
          sx={{
            position: 'absolute',
            inset: -2,
            borderRadius: 2,
            background: 'linear-gradient(45deg, transparent 0%, rgba(0, 255, 255, 0.4) 50%, transparent 100%)',
            opacity: 0.3,
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* FOREGROUND BOTTOM - Holographic Projection */}
      <Box
        sx={{
          position: 'relative',
          width: hologramSize * 2,
          height: hologramSize * 2,
          mb: 2,
          zIndex: 3,
        }}
      >
        <svg width={hologramSize * 2} height={hologramSize * 2} viewBox="0 0 200 200">
          <defs>
            {/* Hologram Glow */}
            <filter id="hologramGlow">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>

            {/* Scan Line Pattern */}
            <pattern id="scanlines" width="2" height="2" patternUnits="userSpaceOnUse">
              <line x1="0" y1="0" x2="2" y2="0" stroke="rgba(0, 255, 255, 0.1)" strokeWidth="0.5"/>
            </pattern>
          </defs>

          {/* Hologram Base */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="rgba(0, 255, 255, 0.05)"
            stroke="rgba(0, 255, 255, 0.4)"
            strokeWidth="1"
          />

          {/* Concentric Animated Rings */}
          {[70, 60, 50, 40, 30, 20].map((r, i) => (
            <g key={i} style={{ transformOrigin: 'center', animation: `${hologramRotate} ${2 + i * 0.3}s linear infinite` }}>
              <circle
                cx="100"
                cy="100"
                r={r}
                fill="none"
                stroke="#00FFFF"
                strokeWidth="1.5"
                strokeDasharray={i % 2 === 0 ? "5,5" : "10,2"}
                opacity={0.4 + i * 0.1}
                filter="url(#hologramGlow)"
              />
            </g>
          ))}

          {/* Rotating Scan Beam */}
          <g style={{ transformOrigin: 'center', animation: `${scanBeam} 2s linear infinite` }}>
            <line
              x1="100"
              y1="100"
              x2="100"
              y2="20"
              stroke="url(#beamGradient)"
              strokeWidth="3"
              filter="url(#hologramGlow)"
            />
            <defs>
              <linearGradient id="beamGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#00FFFF" stopOpacity="0"/>
                <stop offset="100%" stopColor="#00FFFF" stopOpacity="0.9"/>
              </linearGradient>
            </defs>
          </g>

          {/* Data Visualization - Circular Graphs */}
          <g>
            {[0, 120, 240].map((angle, i) => {
              const rad = (angle * Math.PI) / 180;
              const x = 100 + 50 * Math.cos(rad);
              const y = 100 + 50 * Math.sin(rad);
              return (
                <g key={i}>
                  <circle
                    cx={x}
                    cy={y}
                    r="8"
                    fill="rgba(0, 119, 204, 0.3)"
                    stroke="#0077CC"
                    strokeWidth="1.5"
                  />
                  <text
                    x={x}
                    y={y + 1}
                    textAnchor="middle"
                    fill="#1A1A2E"
                    fontSize="8"
                    fontWeight="700"
                    fontFamily="monospace"
                  >
                    {85 + i * 5}%
                  </text>
                </g>
              );
            })}
          </g>

          {/* Floating Data Points */}
          {[...Array(12)].map((_, i) => {
            const angle = (i * 30 * Math.PI) / 180;
            const r = 65;
            const x = 100 + r * Math.cos(angle);
            const y = 100 + r * Math.sin(angle);
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r="2"
                fill="#00A8FF"
                filter="url(#hologramGlow)"
              >
                <animate
                  attributeName="opacity"
                  values="0.3;1;0.3"
                  dur="2s"
                  begin={`${i * 0.15}s`}
                  repeatCount="indefinite"
                />
              </circle>
            );
          })}

          {/* Pulsing Rings */}
          {[0, 1, 2].map((i) => (
            <circle
              key={`pulse-${i}`}
              cx="100"
              cy="100"
              r="30"
              fill="none"
              stroke="#00FFFF"
              strokeWidth="2"
              opacity="0.8"
            >
              <animate
                attributeName="r"
                values="30;50"
                dur="2s"
                begin={`${i * 0.7}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0.8;0"
                dur="2s"
                begin={`${i * 0.7}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))}

          {/* Scan Line Interference */}
          <rect width="200" height="200" fill="url(#scanlines)" opacity="0.3"/>

          {/* Center Icon */}
          <circle
            cx="100"
            cy="100"
            r="12"
            fill="rgba(0, 119, 204, 0.5)"
            stroke="#00FFFF"
            strokeWidth="2"
            filter="url(#hologramGlow)"
          />
          <path
            d="M 100 92 L 108 104 L 92 104 Z"
            fill="#00FFFF"
            opacity="0.8"
          />
        </svg>

        {/* Hologram Uplight Effect */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: '50%',
            transform: 'translateX(-50%)',
            width: hologramSize * 2,
            height: 40,
            background: 'radial-gradient(ellipse at center, rgba(0, 255, 255, 0.3) 0%, transparent 70%)',
            filter: 'blur(20px)',
          }}
        />
      </Box>

      {/* Floating Particles */}
      {[...Array(20)].map((_, i) => (
        <Box
          key={`particle-${i}`}
          sx={{
            position: 'absolute',
            bottom: `${Math.random() * 30}%`,
            left: `${20 + Math.random() * 60}%`,
            width: 3,
            height: 3,
            borderRadius: '50%',
            background: '#00FFFF',
            opacity: 0.6,
            animation: `${particleRise} ${3 + Math.random() * 2}s ease-in infinite`,
            animationDelay: `${Math.random() * 3}s`,
          }}
        />
      ))}

      {/* Loading Text */}
      {message && (
        <Typography
          sx={{
            position: 'absolute',
            bottom: 10,
            color: '#1A1A2E',
            fontSize: '18px',
            fontWeight: 500,
            fontFamily: 'Inter, sans-serif',
            letterSpacing: '0.1em',
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default CleanTechOrbitalSpinner;
